/**
 * 현장 문서 보관함 API 클라이언트.
 * 백엔드 /v0.1/site-management/projects/:id/documents + /documents/:id 와 1:1 매칭.
 *
 * 다운로드는 apiClient(Authorization 헤더)로 blob 을 받아 원본 파일명으로 저장 —
 * cross-origin <a download> 가 파일명을 무시하는 문제를 피한다.
 */
import { API_ENDPOINTS } from '../config/api';
import { apiClient } from './api/apiClient';
import {
  ApiItemResponse,
  ApiListResponse,
  ApiOkResponse,
  ensureOk,
  unwrapItem,
  unwrapList,
} from './api/response';
import { triggerBrowserDownload } from './FileStorageService/downloadMethods';
import { SiteDocument } from '../models/SiteDocument';

export class SiteDocumentService {
  /** category 를 주면 해당 분류만. 목록은 현장 단위이므로 다른 현장 문서는 섞이지 않는다. */
  static async list(projectId: number, category?: string): Promise<SiteDocument[]> {
    const resp = await apiClient.get<ApiListResponse<SiteDocument>>(
      API_ENDPOINTS.SITE_MANAGEMENT.DOCUMENTS.LIST(projectId, category),
    );
    return unwrapList(resp, '문서 목록 조회 실패');
  }

  /**
   * 문서 1건 업로드. 여러 건은 호출 측에서 병렬로 돌린다 — 파일별로 설명·분류가
   * 다르고 실패한 건만 재시도해야 하므로 서버에 한 번에 몰아주지 않는다.
   *
   * onProgress 는 0~100. 업로드 크기를 모르는 환경(total 미제공)에서는 호출되지
   * 않으므로 호출 측은 진행률 없이도 동작해야 한다.
   */
  static async upload(
    projectId: number,
    file: File,
    options: {
      description?: string;
      category?: string;
      onProgress?: (percent: number) => void;
      signal?: AbortSignal;
      /** 주면 그 공지(메모)의 첨부로 매단다 — 파일 보관함 목록에는 뜨지 않는다. */
      scheduleId?: number;
    } = {},
  ): Promise<SiteDocument> {
    const { description, category, onProgress, signal, scheduleId } = options;
    const form = new FormData();
    form.append('file', file, file.name);
    // 폴더째 올리면 file.name 이 "폴더/파일" 이다. multipart 파일명은 서버(Go)가
    // RFC 7578 대로 경로를 떼어 내므로, 보관함에 남길 이름은 따로 보낸다.
    if (file.name.includes('/')) form.append('original_name', file.name);
    if (description) form.append('description', description);
    if (category) form.append('category', category);
    if (scheduleId) form.append('schedule_id', String(scheduleId));
    const resp = await apiClient.post<ApiItemResponse<SiteDocument>>(
      API_ENDPOINTS.SITE_MANAGEMENT.DOCUMENTS.UPLOAD(projectId),
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal,
        onUploadProgress: onProgress
          ? (e) => {
              if (!e.total) return;
              onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
            }
          : undefined,
      },
    );
    return unwrapItem(resp, '문서 업로드 실패');
  }

  /** 공지(메모) 하나에 딸린 첨부 목록. */
  static async listBySchedule(scheduleId: number): Promise<SiteDocument[]> {
    const resp = await apiClient.get<ApiListResponse<SiteDocument>>(
      API_ENDPOINTS.SITE_MANAGEMENT.DOCUMENTS.BY_SCHEDULE(scheduleId),
    );
    return unwrapList(resp, '첨부 목록 조회 실패');
  }

  static async delete(documentId: number): Promise<void> {
    const resp = await apiClient.delete<ApiOkResponse>(
      API_ENDPOINTS.SITE_MANAGEMENT.DOCUMENTS.DELETE(documentId),
    );
    ensureOk(resp, '문서 삭제 실패');
  }

  /** 인증 헤더로 blob 다운로드 후 원본 파일명으로 저장. */
  static async download(doc: SiteDocument): Promise<void> {
    const resp = await apiClient.get(API_ENDPOINTS.SITE_MANAGEMENT.DOCUMENTS.FILE(doc.id), {
      responseType: 'blob',
    });
    // 폴더 업로드 문서는 original_name 이 경로다. <a download> 는 경로 구분자를
    // 제멋대로 치환하므로 파일명만 남긴다.
    triggerBrowserDownload(resp.data as Blob, doc.original_name.split('/').pop() || doc.original_name);
  }

  /**
   * 선택한 문서들을 ZIP 하나로 받는다. 서버가 ids 를 현장 소속으로 다시 걸러
   * 주므로 다른 현장 문서가 섞여 들어갈 일은 없다.
   *
   * zipName 은 저장될 파일명 — 현장 이름을 넣어 어느 현장 묶음인지 알 수 있게 한다.
   */
  static async downloadZip(projectId: number, ids: number[], zipName: string): Promise<void> {
    if (ids.length === 0) throw new Error('다운로드할 문서를 선택하세요');
    const resp = await apiClient.get(API_ENDPOINTS.SITE_MANAGEMENT.DOCUMENTS.ZIP(projectId, ids), {
      responseType: 'blob',
    });
    triggerBrowserDownload(resp.data as Blob, zipName);
  }
}
